import {
  ExecutionStatusEnum,
  getFragmentData,
  makeFragmentData,
} from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';
import type { ResultOf } from '@graphql-typed-document-node/core';

import { ServiceUsagesTotalCardCmp_Total_Fragment } from '../Result';
import { ServiceUsagesTotalCardCmp_TotalCalculation_Fragment } from '..';
import type { GroupTotalCalculationMap } from '../../../../utils/build-group-total-calculation-map.util';

type TotalValues = ResultOf<typeof ServiceUsagesTotalCardCmp_Total_Fragment>;
type Amount = TotalValues['discountAsAmount'];
type TotalCalculationFragment = FragmentType<
  typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
>;

const NEGATIVE_TOTAL_DETAIL = {
  __typename: 'I18nMessage',
  en: 'The quotation total cannot be negative',
  fr: 'Le total du devis ne peut pas être négatif.',
} as const;

const toPercentage = (part: number, whole: number): number =>
  whole ? (part / whole) * 100 : 0;

// Same sum as the back global total (TotalCalculationService.sumTotals).
export const sumTotals = (
  totals: readonly TotalValues[],
): TotalValues | null => {
  const [firstTotal] = totals;

  if (!firstTotal) {
    return null;
  }

  const sum = (pick: (total: TotalValues) => Amount): Amount => ({
    __typename: 'Amount',
    currencyId: pick(firstTotal).currencyId,
    value: totals.reduce((total, current) => total + pick(current).value, 0),
  });

  const undiscountedAmountExcludingVat = sum(
    (total) => total.undiscountedAmountExcludingVat,
  );
  const discountAsAmount = sum((total) => total.discountAsAmount);
  const discountedAmountExcludingVat = sum(
    (total) => total.discountedAmountExcludingVat,
  );
  const vatAsAmount = sum((total) => total.vatAsAmount);

  return {
    __typename: 'Total',
    discountAsAmount,
    discountAsPercentage: toPercentage(
      discountAsAmount.value,
      undiscountedAmountExcludingVat.value,
    ),
    discountedAmountExcludingVat,
    discountedAmountIncludingVat: sum(
      (total) => total.discountedAmountIncludingVat,
    ),
    undiscountedAmountExcludingVat,
    vatAsAmount,
    vatAsPercentage: toPercentage(
      vatAsAmount.value,
      discountedAmountExcludingVat.value,
    ),
  };
};

// Global total of the checked groups only (quotation options): a failed group
// total or a negative sum gives an error status, like the back global total.
// A group total the user may not read makes the sum unreadable too.
export const buildSelectedGroupsTotalCalculation = (
  groupTotalCalculationMap: GroupTotalCalculationMap,
  checkedGroupIds: readonly Id[],
): TotalCalculationFragment | null => {
  const checkedTotalCalculations = checkedGroupIds.map((groupId) =>
    groupTotalCalculationMap.get(groupId),
  );

  if (checkedTotalCalculations.includes(null)) {
    return null;
  }

  const groupTotalCalculations = checkedTotalCalculations
    .filter((totalCalculation) => !!totalCalculation)
    .map((totalCalculation) =>
      getFragmentData(
        ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
        totalCalculation,
      ),
    );

  const failedTotalCalculation = groupTotalCalculations.find(
    ({ status }) => status === ExecutionStatusEnum.Ko,
  );
  const results = groupTotalCalculations.map(({ result }) =>
    result
      ? getFragmentData(ServiceUsagesTotalCardCmp_Total_Fragment, result)
      : null,
  );
  const total = sumTotals(results.filter((result) => !!result));
  const isNegative =
    !!total &&
    (total.discountedAmountExcludingVat.value < 0 ||
      total.discountedAmountIncludingVat.value < 0);
  const isFailed = !!failedTotalCalculation || isNegative;

  return makeFragmentData(
    {
      __typename: 'TotalCalculation',
      automaticResult: null,
      details:
        failedTotalCalculation?.details ??
        (isNegative ? [NEGATIVE_TOTAL_DETAIL] : []),
      result:
        total && !isFailed
          ? {
              __typename: 'Total',
              ...makeFragmentData(
                total,
                ServiceUsagesTotalCardCmp_Total_Fragment,
              ),
            }
          : null,
      status: isFailed ? ExecutionStatusEnum.Ko : ExecutionStatusEnum.Ok,
      totalMode: null,
    },
    ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
  );
};
